import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserX, MapPinOff, PackageX, HandCoins, BellRing, ClipboardCheck, RefreshCcw, CheckCircle2 } from "lucide-react";

const PROBLEMS = [
  { icon: UserX, label: "Customer Unavailable" },
  { icon: MapPinOff, label: "Wrong Address" },
  { icon: PackageX, label: "Delivery Failed" },
  { icon: HandCoins, label: "COD Rejection" },
];
const ACTIONS = [
  { icon: BellRing, label: "Customer Notification" },
  { icon: ClipboardCheck, label: "NDR Action" },
  { icon: RefreshCcw, label: "Reattempt" },
  { icon: CheckCircle2, label: "Delivery" },
];

export default function NdrRtoSection() {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStage((s) => (s + 1) % (ACTIONS.length + 1)), 1400);
    return () => clearInterval(t);
  }, []);
  const delivered = stage === ACTIONS.length;

  return (
    <section className="relative overflow-hidden bg-white py-20 text-shipany-navy sm:py-24" data-testid="ndr-rto-section">
      <div className="mx-auto max-w-7xl px-5">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="text-center">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#F43F5E]">NDR / RTO Control</span>
          <h2 className="font-display mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Turn Failed Deliveries Into Delivered Orders</h2>
        </motion.div>

        <div className="mt-12 grid items-center gap-6 lg:grid-cols-[1fr_auto_1fr]">
          {/* problems */}
          <div className="grid grid-cols-2 gap-3">
            {PROBLEMS.map((p, i) => (
              <motion.div key={p.label} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-red-100 bg-red-50/60 p-4 text-center" data-testid={`ndr-problem-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p.icon className="mx-auto h-6 w-6 text-[#F43F5E]" />
                <p className="mt-2 text-xs font-bold text-slate-700">{p.label}</p>
                <span className="mono mt-1 inline-block rounded-full bg-[#F43F5E]/10 px-2 py-0.5 text-[9px] font-bold text-[#F43F5E]">RTO RISK</span>
              </motion.div>
            ))}
          </div>

          {/* intervention pipeline */}
          <div className="flex flex-row items-center justify-center gap-2 lg:flex-col" data-testid="ndr-intervention">
            {ACTIONS.map((a, i) => (
              <div key={a.label} className="flex items-center gap-2 lg:flex-col">
                <motion.div animate={stage > i || delivered ? { scale: 1, borderColor: "#10B981", backgroundColor: "rgba(16,185,129,0.12)" } : { scale: 0.92 }}
                  className="flex h-12 w-12 items-center justify-center rounded-xl border-2 border-slate-200 bg-white shadow-sm transition-colors">
                  <a.icon className={`h-5 w-5 ${stage > i || delivered ? "text-[#10B981]" : "text-slate-400"}`} />
                </motion.div>
                <span className={`hidden text-[9px] font-bold uppercase tracking-wide lg:block ${stage > i || delivered ? "text-[#10B981]" : "text-slate-400"}`}>{a.label}</span>
                {i < ACTIONS.length - 1 && <span className={`h-0.5 w-6 rounded lg:h-6 lg:w-0.5 ${stage > i ? "bg-[#10B981]" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          {/* result */}
          <AnimatePresence mode="wait">
            <motion.div key={delivered ? "done" : "pending"} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
              className={`rounded-3xl border p-8 text-center transition-colors ${delivered ? "border-[#10B981]/40 bg-[#10B981]/5" : "border-slate-200 bg-[#F7FAFC]"}`}
              data-testid="ndr-result">
              <CheckCircle2 className={`mx-auto h-12 w-12 ${delivered ? "text-[#10B981]" : "text-slate-300"}`} />
              <p className={`font-display mt-4 text-xl font-extrabold tracking-tight sm:text-2xl ${delivered ? "text-[#10B981]" : "text-slate-400"}`}>
                MORE DELIVERIES.<br />FEWER RETURNS.
              </p>
              <p className="mt-2 text-xs text-slate-500">ShipAny intervenes before an NDR becomes an RTO.</p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
